import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { progressAPI } from '../../services/api';

type Tab = 'body' | 'diet' | 'workout';
type BodyMetric = 'weight' | 'bodyFat' | 'waist' | 'chest' | 'arms' | 'hips' | 'thighs';

const METRIC_COLORS: Record<BodyMetric, string> = {
  weight: '#4f46e5',
  bodyFat: '#f59e0b',
  waist: '#ef4444',
  chest: '#10b981',
  arms: '#8b5cf6',
  hips: '#ec4899',
  thighs: '#06b6d4',
};

const METRIC_UNITS: Record<BodyMetric, string> = {
  weight: 'kg',
  bodyFat: '%',
  waist: 'cm',
  chest: 'cm',
  arms: 'cm',
  hips: 'cm',
  thighs: 'cm',
};

function formatDate(d: string) {
  const date = new Date(d + 'T00:00:00');
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function Progress() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('body');
  const [workoutLogs, setWorkoutLogs] = useState<any[]>([]);
  const [dietLogs, setDietLogs] = useState<any[]>([]);
  const [bodyMeasurements, setBodyMeasurements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [bodyForm, setBodyForm] = useState({ weight: '', bodyFat: '', chest: '', waist: '', arms: '', hips: '', thighs: '' });
  const [saving, setSaving] = useState(false);

  // Chart metric selection
  const [selectedMetrics, setSelectedMetrics] = useState<BodyMetric[]>(['weight']);

  useEffect(() => {
    if (user?._id) loadData();
  }, [user]);

  const loadData = async () => {
    if (!user?._id) return;
    setLoading(true);
    try {
      const [wRes, dRes, bRes] = await Promise.all([
        progressAPI.getWorkoutLogs(user._id),
        progressAPI.getDietLogs(user._id),
        progressAPI.getBodyMeasurements(user._id),
      ]) as any[];
      setWorkoutLogs(wRes?.logs || []);
      setDietLogs(dRes?.logs || []);
      setBodyMeasurements(bRes?.measurements || []);
    } catch (err) {
      console.error('Load progress error:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveBodyMeasurement = async () => {
    if (!user?._id || !bodyForm.weight) return;
    setSaving(true);
    try {
      await progressAPI.createBodyMeasurement(user._id, {
        date: new Date().toLocaleDateString('en-CA'),
        weight: Number(bodyForm.weight),
        bodyFat: bodyForm.bodyFat ? Number(bodyForm.bodyFat) : undefined,
        chest: bodyForm.chest ? Number(bodyForm.chest) : undefined,
        waist: bodyForm.waist ? Number(bodyForm.waist) : undefined,
        arms: bodyForm.arms ? Number(bodyForm.arms) : undefined,
        hips: bodyForm.hips ? Number(bodyForm.hips) : undefined,
        thighs: bodyForm.thighs ? Number(bodyForm.thighs) : undefined,
      });
      setBodyForm({ weight: '', bodyFat: '', chest: '', waist: '', arms: '', hips: '', thighs: '' });
      loadData();
    } catch (err) {
      console.error('Save measurement error:', err);
    } finally {
      setSaving(false);
    }
  };

  const deleteMeasurement = async (id: string) => {
    if (!user?._id) return;
    await progressAPI.deleteBodyMeasurement(user._id, id);
    loadData();
  };

  const deleteDietLog = async (id: string) => {
    if (!user?._id) return;
    await progressAPI.deleteDietLog(user._id, id);
    loadData();
  };

  const deleteWorkoutLog = async (id: string) => {
    if (!user?._id) return;
    await progressAPI.deleteWorkoutLog(user._id, id);
    loadData();
  };

  const toggleMetric = (metric: BodyMetric) => {
    setSelectedMetrics(prev =>
      prev.includes(metric)
        ? prev.length > 1 ? prev.filter(m => m !== metric) : prev
        : [...prev, metric]
    );
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

  // Body chart data: all measurements sorted ascending, last 30
  const sortedBody = [...bodyMeasurements]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(-30);

  const bodyChartData = sortedBody.map(m => ({
    date: m.date,
    weight: m.weight || null,
    bodyFat: m.bodyFat || null,
    waist: m.waist || null,
    chest: m.chest || null,
    arms: m.arms || null,
    hips: m.hips || null,
    thighs: m.thighs || null,
  }));

  // Available metrics (ones that have at least one data point)
  const availableMetrics = (['weight', 'bodyFat', 'waist', 'chest', 'arms', 'hips', 'thighs'] as BodyMetric[])
    .filter(m => sortedBody.some(b => b[m]));

  // Workout volume chart: total volume (sets * reps * weight) per session
  const workoutChartData = [...workoutLogs]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(-30)
    .map(log => {
      let totalVolume = 0;
      let totalSets = 0;
      log.exercises?.forEach((ex: any) => {
        ex.sets?.forEach((s: any) => {
          if (s.completed) {
            totalSets++;
            totalVolume += (s.reps || 0) * (s.weight || 0);
          }
        });
      });
      return {
        date: log.date,
        volume: Math.round(totalVolume),
        sets: totalSets,
        exercises: log.exercises?.length || 0,
        name: log.name || 'Workout',
      };
    });

  // Diet chart: calories + protein over time
  const dietChartData = [...dietLogs]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(-30)
    .map(log => ({
      date: log.date,
      calories: log.totalCalories || 0,
      protein: log.totalProtein || 0,
      carbs: log.totalCarbs || 0,
      fat: log.totalFat || 0,
    }));

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('progress.title')}</h1>

      <div className="flex gap-2 mb-6 bg-white rounded-xl shadow-sm p-1">
        {(['body', 'diet', 'workout'] as Tab[]).map((t2) => (
          <button key={t2} onClick={() => setTab(t2)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t2 ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
            {t(`progress.tabs.${t2}`)}
          </button>
        ))}
      </div>

      {/* ── BODY TAB ── */}
      {tab === 'body' && (
        <div className="space-y-4">
          {/* Evolution chart with metric selector */}
          {bodyChartData.length >= 2 && (
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('progress.charts.bodyEvolution')}</h3>

              {/* Metric toggles */}
              <div className="flex flex-wrap gap-2 mb-4">
                {availableMetrics.map(metric => (
                  <button
                    key={metric}
                    onClick={() => toggleMetric(metric)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                      selectedMetrics.includes(metric)
                        ? 'text-white border-transparent'
                        : 'text-gray-500 border-gray-300 bg-white hover:bg-gray-50'
                    }`}
                    style={selectedMetrics.includes(metric) ? { backgroundColor: METRIC_COLORS[metric] } : undefined}
                  >
                    {t(`progress.metrics.${metric}`)} ({METRIC_UNITS[metric]})
                  </button>
                ))}
              </div>

              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={bodyChartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: '#9ca3af' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatDate}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    labelFormatter={d => new Date(d + 'T00:00:00').toLocaleDateString()}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    formatter={(v: any, name: any) => {
                      const metric = name as BodyMetric;
                      return [`${v} ${METRIC_UNITS[metric] || ''}`, t(`progress.metrics.${metric}`)];
                    }}
                  />
                  <Legend
                    formatter={(value: string) => t(`progress.metrics.${value}`)}
                    wrapperStyle={{ fontSize: 11 }}
                  />
                  {selectedMetrics.map(metric => (
                    <Line
                      key={metric}
                      type="monotone"
                      dataKey={metric}
                      stroke={METRIC_COLORS[metric]}
                      strokeWidth={2}
                      dot={{ r: 3, fill: METRIC_COLORS[metric] }}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-gray-800 mb-4">{t('progress.addEntry')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><label className="text-xs text-gray-500">{t('progress.weight')} (kg)*</label><input value={bodyForm.weight} onChange={e => setBodyForm(f => ({...f, weight: e.target.value}))} className={inputClass} type="number" step="0.1" /></div>
              <div><label className="text-xs text-gray-500">{t('progress.bodyMeasurements.bodyFat')} (%)</label><input value={bodyForm.bodyFat} onChange={e => setBodyForm(f => ({...f, bodyFat: e.target.value}))} className={inputClass} type="number" /></div>
              <div><label className="text-xs text-gray-500">{t('progress.bodyMeasurements.chest')} (cm)</label><input value={bodyForm.chest} onChange={e => setBodyForm(f => ({...f, chest: e.target.value}))} className={inputClass} type="number" /></div>
              <div><label className="text-xs text-gray-500">{t('progress.bodyMeasurements.waist')} (cm)</label><input value={bodyForm.waist} onChange={e => setBodyForm(f => ({...f, waist: e.target.value}))} className={inputClass} type="number" /></div>
              <div><label className="text-xs text-gray-500">{t('progress.bodyMeasurements.arms')} (cm)</label><input value={bodyForm.arms} onChange={e => setBodyForm(f => ({...f, arms: e.target.value}))} className={inputClass} type="number" /></div>
              <div><label className="text-xs text-gray-500">{t('progress.bodyMeasurements.hips')} (cm)</label><input value={bodyForm.hips} onChange={e => setBodyForm(f => ({...f, hips: e.target.value}))} className={inputClass} type="number" /></div>
              <div><label className="text-xs text-gray-500">{t('progress.bodyMeasurements.thighs')} (cm)</label><input value={bodyForm.thighs} onChange={e => setBodyForm(f => ({...f, thighs: e.target.value}))} className={inputClass} type="number" /></div>
            </div>
            <button onClick={saveBodyMeasurement} disabled={saving || !bodyForm.weight} className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
              {saving ? t('common.submitting') : t('progress.saveMeasurement')}
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-gray-800 mb-4">{t('progress.history')}</h3>
            {bodyMeasurements.length === 0 ? <p className="text-gray-500 text-sm">{t('progress.noEntries')}</p> : (
              <div className="space-y-2">
                {[...bodyMeasurements].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((m: any) => (
                  <div key={m._id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg text-sm">
                    <div>
                      <span className="font-medium">{m.date}</span>
                      <span className="ml-4 text-gray-600">{m.weight}kg</span>
                      {m.bodyFat && <span className="ml-2 text-gray-500">{m.bodyFat}% BF</span>}
                      {m.waist && <span className="ml-2 text-gray-400 text-xs">waist {m.waist}cm</span>}
                    </div>
                    <button onClick={() => deleteMeasurement(m._id)} className="text-red-500 text-xs hover:text-red-700">{t('common.delete')}</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── DIET TAB ── */}
      {tab === 'diet' && (
        <div className="space-y-4">
          {/* Calorie & macro trend chart */}
          {dietChartData.length >= 2 && (
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('progress.charts.caloriesTrend')}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={dietChartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: '#9ca3af' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatDate}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    labelFormatter={d => new Date(d + 'T00:00:00').toLocaleDateString()}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="calories" fill="#4f46e5" name={t('progress.charts.calories')} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>

              {/* Macro breakdown line chart */}
              <h3 className="text-sm font-semibold text-gray-700 mb-3 mt-6">{t('progress.charts.macrosTrend')}</h3>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={dietChartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: '#9ca3af' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatDate}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    labelFormatter={d => new Date(d + 'T00:00:00').toLocaleDateString()}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    formatter={(v: any, name: any) => [`${v}g`, name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="protein" stroke="#ef4444" strokeWidth={2} name={t('progress.charts.protein')} dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="carbs" stroke="#f59e0b" strokeWidth={2} name={t('progress.charts.carbs')} dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="fat" stroke="#8b5cf6" strokeWidth={2} name={t('progress.charts.fat')} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="font-semibold text-indigo-800 text-sm">{t('progress.charts.logTodayFood')}</p>
              <p className="text-indigo-600 text-xs mt-0.5">{t('progress.charts.logTodayFoodDesc')}</p>
            </div>
            <button
              onClick={() => navigate('/diet-plans')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex-shrink-0 ml-4"
            >
              {t('progress.charts.goToDiet')}
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-gray-800 mb-4">{t('progress.dietLogHistory')}</h3>
            {dietLogs.length === 0 ? <p className="text-gray-500 text-sm">{t('progress.noFoodEntries')}</p> : (
              <div className="space-y-2">
                {[...dietLogs].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((log: any) => (
                  <div key={log._id} className="p-3 bg-gray-50 rounded-lg text-sm">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium">{log.date}</span>
                      <div className="flex gap-3 items-center">
                        <span className="text-gray-500">{log.totalCalories} kcal · P {log.totalProtein}g · C {log.totalCarbs}g · F {log.totalFat}g</span>
                        <button onClick={() => deleteDietLog(log._id)} className="text-red-500 text-xs hover:text-red-700">{t('common.delete')}</button>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 truncate">{log.entries?.map((e: any) => e.name).join(', ')}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── WORKOUT TAB ── */}
      {tab === 'workout' && (
        <div className="space-y-4">
          {/* Workout volume evolution chart */}
          {workoutChartData.length >= 2 && (
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('progress.charts.volumeEvolution')}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={workoutChartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: '#9ca3af' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatDate}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    labelFormatter={d => new Date(d + 'T00:00:00').toLocaleDateString()}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    formatter={(v: any, name: any) => {
                      if (name === 'volume') return [`${v.toLocaleString()} kg`, t('progress.charts.totalVolume')];
                      return [`${v}`, name];
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11 }}
                    formatter={(value: string) => {
                      if (value === 'volume') return t('progress.charts.totalVolume');
                      if (value === 'sets') return t('progress.charts.totalSets');
                      return value;
                    }}
                  />
                  <Bar dataKey="volume" fill="#4f46e5" name="volume" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>

              {/* Sets per workout trend */}
              <h3 className="text-sm font-semibold text-gray-700 mb-3 mt-6">{t('progress.charts.setsTrend')}</h3>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={workoutChartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: '#9ca3af' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatDate}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    labelFormatter={d => new Date(d + 'T00:00:00').toLocaleDateString()}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Line type="monotone" dataKey="sets" stroke="#10b981" strokeWidth={2} name={t('progress.charts.totalSets')} dot={{ r: 3, fill: '#10b981' }} />
                  <Line type="monotone" dataKey="exercises" stroke="#f59e0b" strokeWidth={2} name={t('progress.charts.exercisesCount')} dot={{ r: 3, fill: '#f59e0b' }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="font-semibold text-indigo-800 text-sm">{t('progress.charts.logWorkout')}</p>
              <p className="text-indigo-600 text-xs mt-0.5">{t('progress.charts.logWorkoutDesc')}</p>
            </div>
            <button
              onClick={() => navigate('/workout-plans?tab=log')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex-shrink-0 ml-4"
            >
              {t('progress.charts.logWorkoutBtn')}
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-gray-800 mb-4">{t('progress.workoutHistory')}</h3>
            {workoutLogs.length === 0 ? (
              <p className="text-gray-500 text-sm">{t('progress.noEntries')}</p>
            ) : (
              <div className="space-y-2">
                {[...workoutLogs].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((log: any) => (
                  <div key={log._id} className="p-3 bg-gray-50 rounded-lg text-sm">
                    <div className="flex justify-between items-center mb-1">
                      <div>
                        <span className="font-medium">{log.name || 'Workout'}</span>
                        <span className="ml-3 text-gray-500 text-xs">{log.date}</span>
                        <span className="ml-3 text-gray-500">{log.exercises?.length || 0} {t('progress.exercises')}</span>
                      </div>
                      <button onClick={() => deleteWorkoutLog(log._id)} className="text-red-500 text-xs hover:text-red-700">{t('common.delete')}</button>
                    </div>
                    {log.exercises?.slice(0, 3).map((ex: any, idx: number) => (
                      <div key={idx} className="text-xs text-gray-500">{ex.name}: {ex.sets?.filter((s: any) => s.completed).length || 0} sets</div>
                    ))}
                    {log.exercises?.length > 3 && (
                      <div className="text-xs text-gray-400">+{log.exercises.length - 3} more exercises</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
